import { IRequest } from 'legacy/lut/classes/models/onboarding/IRequest'
import { RequestStatus } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { useEffect, useState } from 'react'

import {
  createMDOffboardingRequest,
  findMDOffboardingRequest,
  getMDOffboardingRequest,
  rejectMDOffboardingRequest,
  reopenMDOffboardingRequest,
} from '../api/mdOffboardingRequestApi'

type RequestAction = 'create' | 'reject' | 'reopen'

export const useMDOffboardingRequest = (partnerId: string, requestor: string) => {
  const [request, setRequest] = useState<IRequest | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [pendingAction, setPendingAction] = useState<RequestAction | null>(null)
  const [actionError, setActionError] = useState<RequestAction | null>(null)

  useEffect(() => {
    // Aborted on partnerId change / reload / unmount, so a stale response is ignored
    const controller = new AbortController()
    setIsLoading(true)
    findMDOffboardingRequest(partnerId)
      .then((data) => {
        if (!controller.signal.aborted) {
          setRequest(data)
          setLoadError(false)
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load MD off-boarding request', error)
          setLoadError(true)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })
    return () => controller.abort()
  }, [partnerId, reloadKey])

  const loadRequest = () => setReloadKey((key) => key + 1)

  const refreshRequest = async (requestId: number) => {
    try {
      setRequest(await getMDOffboardingRequest(requestId, partnerId))
      setLoadError(false)
    } catch (error) {
      console.error('Failed to reload MD off-boarding request', error)
      setLoadError(true)
    }
  }

  const runAction = async (action: RequestAction, operation: () => Promise<number>) => {
    setPendingAction(action)
    setActionError(null)
    try {
      const requestId = await operation()
      await refreshRequest(requestId)
      return true
    } catch (error) {
      console.error(`Failed to ${action} MD off-boarding request`, error)
      setActionError(action)
      return false
    } finally {
      setPendingAction(null)
    }
  }

  const createRequest = () =>
    runAction('create', () => createMDOffboardingRequest(partnerId, requestor))

  const rejectRequest = async (comment: string) => {
    if (!request) {
      return false
    }
    const requestId = request.EntryId
    return runAction('reject', async () => {
      await rejectMDOffboardingRequest(requestId, comment)
      return requestId
    })
  }

  const reopenRequest = async () => {
    if (!request) {
      return false
    }
    const requestId = request.EntryId
    return runAction('reopen', async () => {
      await reopenMDOffboardingRequest(requestId)
      return requestId
    })
  }

  return {
    request,
    isOffboarded: request?.Status === RequestStatus.Done,
    isLoading,
    loadError,
    pendingAction,
    actionError,
    loadRequest,
    createRequest,
    rejectRequest,
    reopenRequest,
    clearActionError: () => setActionError(null),
  }
}

export type MDOffboardingRequestState = ReturnType<typeof useMDOffboardingRequest>
